import React, { useState } from 'react';
import { Calculator, BookOpen, Award, Layers } from 'lucide-react';
import { FORMULA_EQUATIONS } from '../data/formulas';
import { FormulaReviewQueueView } from '../components/FormulaReviewQueueView';
import { ActiveTab } from '../components/formula-graph/types';
import { useFormulaSolver } from '../components/formula-graph/useFormulaSolver';
import { FormulaSelectedBadges } from '../components/formula-graph/FormulaSelectedBadges';
import { FormulaVariablePicker } from '../components/formula-graph/FormulaVariablePicker';
import { FormulaDeductionView } from '../components/formula-graph/FormulaDeductionView';
import { FormulaCatalogView } from '../components/formula-graph/FormulaCatalogView';

export const FormulaGraphPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('SOLVER');

  const {
    selectedKnowns,
    selectedTarget,
    activeChainIndex,
    setActiveChainIndex,
    finderResult,
    activeChain,
    questionsByFormulaId,
    toggleKnown,
    removeKnown,
    addKnown,
    setTarget,
    clearAll,
  } = useFormulaSolver();

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] bg-paper-50 dark:bg-study-slate overflow-hidden">
      {/* Top Controls Bar */}
      <header className="shrink-0 flex flex-wrap items-center justify-between gap-3 border-b border-paper-300 bg-paper-100 px-6 py-2.5 dark:border-study-line dark:bg-study-panel">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h1 className="font-serif text-base font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
              Prontuário & Localizador de Fórmulas
            </h1>
          </div>
          <span className="hidden sm:inline-block rounded-full bg-paper-200 px-2.5 py-0.5 text-[10px] font-mono text-zinc-600 dark:bg-study-card dark:text-zinc-400 border border-paper-300 dark:border-study-line">
            {FORMULA_EQUATIONS.length} Relações • Matriz Anexo VI
          </span>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-1 rounded-lg border border-paper-300 bg-paper-50 p-1 dark:border-study-line dark:bg-study-card">
          <button
            type="button"
            onClick={() => setActiveTab('SOLVER')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-semibold transition ${
              activeTab === 'SOLVER'
                ? 'bg-zinc-900 text-white shadow-sm dark:bg-zinc-100 dark:text-zinc-900'
                : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200'
            }`}
          >
            <Layers className="h-3.5 w-3.5" />
            <span>Localizador & Dedução</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('CATALOG')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-semibold transition ${
              activeTab === 'CATALOG'
                ? 'bg-zinc-900 text-white shadow-sm dark:bg-zinc-100 dark:text-zinc-900'
                : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200'
            }`}
          >
            <BookOpen className="h-3.5 w-3.5" />
            <span>Catálogo do Prontuário</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('REVIEW_QUEUE')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-semibold transition ${
              activeTab === 'REVIEW_QUEUE'
                ? 'bg-zinc-900 text-white shadow-sm dark:bg-zinc-100 dark:text-zinc-900'
                : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200'
            }`}
          >
            <Award className="h-3.5 w-3.5" />
            <span>Curadoria de Cadernos</span>
          </button>
        </div>
      </header>

      {/* ── Tab 1: SOLVER (Split Side-by-Side View) ── */}
      {activeTab === 'SOLVER' && (
        <div className="flex flex-1 flex-col overflow-hidden lg:flex-row">
          {/* Left Panel: Variable Selection & Picker Tray */}
          <aside className="flex w-full flex-col border-b border-paper-300 bg-paper-50 p-4 dark:border-study-line dark:bg-study-slate lg:w-[45%] xl:w-[42%] lg:border-b-0 lg:border-r overflow-hidden">
            <FormulaSelectedBadges
              selectedKnowns={selectedKnowns}
              selectedTarget={selectedTarget}
              onRemoveKnown={removeKnown}
              onClearTarget={() => setTarget('')}
              onClearAll={clearAll}
            />
            <div className="mt-3 pt-3 border-t border-paper-200 dark:border-study-line flex-1 overflow-hidden">
              <FormulaVariablePicker
                selectedKnowns={selectedKnowns}
                selectedTarget={selectedTarget}
                onToggleKnown={toggleKnown}
                onSetTarget={setTarget}
              />
            </div>
          </aside>

          {/* Right Panel: Live Deduction & Step Visualizer */}
          <main className="flex flex-1 flex-col overflow-hidden p-4">
            <FormulaDeductionView
              selectedKnowns={selectedKnowns}
              selectedTarget={selectedTarget}
              finderResult={finderResult}
              activeChain={activeChain}
              activeChainIndex={activeChainIndex}
              setActiveChainIndex={setActiveChainIndex}
              questionsByFormulaId={questionsByFormulaId}
              onAddKnown={addKnown}
              onSetTarget={setTarget}
            />
          </main>
        </div>
      )}

      {/* ── Tab 2: CATALOG (Formula Prontuário Browser) ── */}
      {activeTab === 'CATALOG' && (
        <FormulaCatalogView
          onSelectFormulaForSolver={(eq) => {
            clearAll();
            eq.variables.slice(1).forEach((vid) => addKnown(vid));
            if (eq.variables.length > 0) {
              setTarget(eq.variables[0]);
            }
            setActiveTab('SOLVER');
          }}
        />
      )}

      {/* ── Tab 3: REVIEW QUEUE (Ingestion & Curatorship) ── */}
      {activeTab === 'REVIEW_QUEUE' && (
        <div className="flex-1 overflow-y-auto p-6">
          <FormulaReviewQueueView />
        </div>
      )}
    </div>
  );
};
